/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { GENDERS } from 'dpl/shared/constants/breeder_app';
import { BreederProfileLitterPropTypes } from 'dpl/shared/utils/customPropTypes';
import { getOrderedParents } from 'dpl/shared/utils/breeder_app';
import { isMobileUA } from 'dpl/shared/utils';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import Icon from 'dpl/common/components/Icon';
import ParentModal from 'dpl/dog_modals/parent_modal/components/ParentModal';
import { LITTER_STATES } from 'dpl/shared/constants/shared';

import BreederProfileFamilyOverlayParentItem from './BreederProfileFamilyOverlayParentItem';
import BreederProfileFamilyOverlayPuppyItem from './BreederProfileFamilyOverlayPuppyItem';

const { MALE } = GENDERS;

export const imageDimensions = isMobileUA()
  ? {
      width: `${document.body.getBoundingClientRect().width}px`,
      height: '267px'
    }
  : { width: '800px', height: '533px' };

export function BreederProfileFamilyOverlayTree({
  asPuppy,
  currentPuppy,
  litter,
  onPuppyClick
}) {
  const puppies = [...litter.puppies];
  const idToName = {};
  let boyCount = 1;
  let girlCount = 1;
  puppies.forEach(({ gender, id, name }) => {
    idToName[id] =
      name || (gender === MALE ? `Boy ${boyCount++}` : `Girl ${girlCount++}`);
  });

  const [parentId, setParentId] = useState(null);
  const showPuppies =
    puppies.length > 0 && (litter.status === LITTER_STATES.BORN || asPuppy);
  return (
    <div className="BreederProfileFamilyOverlayTree">
      <h5 className="font-20 mb4">Parents</h5>
      <ul className="mb2 row">
        {getOrderedParents(litter.parents).map(p => (
          <li className="col-12" key={p.id}>
            <BreederProfileFamilyOverlayParentItem
              type={p.type}
              gender={p.gender}
              name={p.name}
              healthTierTitle={p.health_tier_title}
              photographUrl={p.profile_photo_url}
              subtitle={p.gender === MALE ? 'Dad' : 'Mom'}
              onClick={() => setParentId(p.id)}
            />
          </li>
        ))}
      </ul>

      {showPuppies && (
        <>
          <h5 className="font-20 mb4">{asPuppy ? 'Siblings' : 'Puppies'}</h5>
          <ul>
            {puppies.map(p => {
              if (p.id === currentPuppy.id) {
                return null;
              }

              const subtitle = p.gender === MALE ? 'Brother' : 'Sister';
              return (
                <li className="mb2" key={p.id}>
                  <BreederProfileFamilyOverlayPuppyItem
                    name={idToName[p.id]}
                    onClick={() => onPuppyClick(p.id, litter.id)}
                    photographUrl={p.profile_photo_url}
                    reserved={p.reserved}
                    subtitle={asPuppy ? subtitle : null}
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}
      <ParentModal
        dogId={parentId}
        isOpen={Boolean(parentId)}
        onClose={() => setParentId(null)}
      />
    </div>
  );
}
BreederProfileFamilyOverlayTree.propTypes = {
  asPuppy: PropTypes.bool,
  currentPuppy: PropTypes.shape({
    id: PropTypes.number
  }),
  onPuppyClick: PropTypes.func.isRequired,
  litter: PropTypes.shape(BreederProfileLitterPropTypes).isRequired
};

BreederProfileFamilyOverlayTree.defaultProps = {
  asPuppy: false,
  currentPuppy: {}
};

export function BreederProfileFamilyOverlayDetailsSection({ children, title }) {
  return (
    <div>
      <h5 className="font-20 pb3 mb4 bb b--light-gray">{title}</h5>
      <div className="mb8">{children}</div>
    </div>
  );
}

BreederProfileFamilyOverlayDetailsSection.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired
};

export function BreederProfileFamilyOverlayDetailsSectionItems({ details }) {
  return (
    <div>
      {details.map(
        ([label, value]) =>
          Boolean(value) && (
            <div key={label} className="row f2 pv1 ph1">
              <p className="col-sm-4 mb1 fw-medium">{label}</p>
              <p className="col-sm-8 mb1 secondary">{value}</p>
            </div>
          )
      )}
    </div>
  );
}

BreederProfileFamilyOverlayDetailsSectionItems.propTypes = {
  details: PropTypes.arrayOf(PropTypes.array).isRequired
};

export default function BreederProfileFamilyOverlay({
  children,
  contentClassName,
  onClose,
  scrollableContent
}) {
  return (
    <div className="BreederProfileFamilyOverlay">
      <MiniOverlayModal
        hideCloseButton
        maxWidth="800px"
        scrollableContent={scrollableContent}
        zIndexClass="z-99999"
        onCloseClick={onClose}
        contentClassName={classnames('bg-white', contentClassName)}
      >
        <button type="button" className="dn-l pl2 pt6 pb2" onClick={onClose}>
          <Icon className="black-50" name="close" />
        </button>
        {children}
      </MiniOverlayModal>
    </div>
  );
}

BreederProfileFamilyOverlay.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  contentClassName: PropTypes.string,
  scrollableContent: PropTypes.bool
};

BreederProfileFamilyOverlay.defaultProps = {
  contentClassName: 'self-stretch',
  scrollableContent: false
};
